import { FC, useCallback, useMemo } from 'react';

import { AdElementText, isDisplayOptionsOddsType } from '@adflow/types';
import { Flex, FormControl } from '@chakra-ui/react';
import { useStore } from '../../../../hooks/useStore';
import OddsFormat from './OddsFormat';

type OddsControlsProps = {
  selectedElement: AdElementText;
};

export const isOddsPresent = (textElement: AdElementText) => {
  if (!textElement.sources[0]) return false;

  const oddsSources = textElement.sources.filter((source) => source.name.includes('ODDS'));
  return oddsSources.length > 0;
};

const OddsControls: FC<OddsControlsProps> = ({ selectedElement }) => {
  const store = useStore((state) => ({
    displayOptions: state.displayOptions,
    setElementOddsDisplayOptions: state.setElementOddsDisplayOptions,
  }));

  const oddsDisplayOptions = useMemo(() => {
    return selectedElement.data.odds || store.displayOptions.odds;
  }, [selectedElement.data.odds, store.displayOptions.odds]);

  const handleChangeOddsFormat: React.ChangeEventHandler<HTMLSelectElement> = useCallback(
    (event) => {
      const oddsFormat = event.target.value;
      if (isDisplayOptionsOddsType(oddsFormat)) {
        const isPotentialWinnings = oddsFormat === 'potentialWinnings';
        store.setElementOddsDisplayOptions(selectedElement.id, {
          ...oddsDisplayOptions,
          type: oddsFormat,
          stake: isPotentialWinnings ? 10 : null,
          currency: isPotentialWinnings ? 'GBP' : null,
        });
      } else {
        store.setElementOddsDisplayOptions(selectedElement.id, {
          ...oddsDisplayOptions,
          type: 'decimalOdds',
          stake: null,
          currency: null,
        });
      }
    },
    [oddsDisplayOptions, selectedElement.id, store],
  );

  const setOddsStakeCurrency: React.ChangeEventHandler<HTMLSelectElement> = useCallback(
    (event) => {
      const currency = event.target.value;
      store.setElementOddsDisplayOptions(selectedElement.id, {
        ...oddsDisplayOptions,
        type: 'potentialWinnings',
        currency,
      });
    },
    [oddsDisplayOptions, selectedElement.id, store],
  );

  const setOddsStakeAmount: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const stake = parseInt(event.target.value);

      if (!isNaN(stake)) {
        store.setElementOddsDisplayOptions(selectedElement.id, {
          ...oddsDisplayOptions,
          type: 'potentialWinnings',
          stake,
        });
      }
    },
    [oddsDisplayOptions, selectedElement.id, store],
  );

  const setCalculateDecimalPlaces = useCallback(
    (isChecked: boolean) => {
      const newOptions = {
        type: oddsDisplayOptions?.type || 'potentialWinnings',
        currency: oddsDisplayOptions?.currency,
        stake: oddsDisplayOptions?.stake,
        calculateDecimalPlaces: isChecked,
      };
      store.setElementOddsDisplayOptions(selectedElement.id, newOptions);
    },
    [oddsDisplayOptions, selectedElement.id, store],
  );

  return (
    <FormControl alignItems="center" mt={5} mb={2} mr={0}>
      <Flex direction="column" gap="3">
        <OddsFormat
          oddsDisplayOptions={oddsDisplayOptions}
          handleChangeOddsFormat={handleChangeOddsFormat}
          setOddsStakeAmount={setOddsStakeAmount}
          setOddsStakeCurrency={setOddsStakeCurrency}
          setCalculateDecimalPlaces={setCalculateDecimalPlaces}
        />
      </Flex>
    </FormControl>
  );
};

OddsControls.displayName = 'OddsControls';

export default OddsControls;
